import type { I18n } from "@/i18n";
import type { RouterI18nParameters } from "@/locales/fr/router";
import type { SiteId } from "@/types/common/ids";
import type { QueryClient } from "@tanstack/vue-query";
import type { RouteLocationNormalizedLoaded } from "vue-router";

import { head } from "es-toolkit";

import { siteDetailsOptions } from "@/app/site/routes/[siteId]/hooks/use-site-details";
import { kiliOrganizationDetailsOptions } from "@/hooks/kili/organizations/queries/use-kili-organization-details-query.ts";
import { chain } from "@/lib/chain";
import { warnUnreachable } from "@/types/utils";

const APP_NAME = "Voltacall";

const PARAMETER_ORDER: Record<keyof RouterI18nParameters, number> = {
  organizationId: 0,
  siteId: 1,
};

// Computing the right route title should *never* be in the critical path
export const formatRouteTitle =
  (i18n: I18n, queryClient: QueryClient) =>
  (to: Pick<RouteLocationNormalizedLoaded, "name" | "params">): string => {
    // Accessing i18n.global.t directly triggers TS infinite instantiation on dynamic keys.
    // This whole function is very dynamic / non-typesafe so it's definitely ok to deal with 'any' down there.
    const intl = i18n.global as any;
    const { t, te } = intl;
    const title = t(`routing.titles.${String(to.name)}`);

    const parameters = chain(Object.entries(to.params))
      .filterMap(([_name, _value]) => {
        // e.g 'name' := 'siteId' , value := '2134'
        const name: keyof RouterI18nParameters = _name as keyof RouterI18nParameters;
        const value: string = (Array.isArray(_value) ? head(_value) : _value) || "";

        if (!value) return undefined;

        // Generic parameter formatting (e.g "Site 2134")
        const key = `routing.parameters.${name}`;
        const genericLabel = te(key) ? t(key, { [name]: value }) : undefined;

        // Customized parameter formatting (e.g "firstName lastName")
        const customizedLabel = formatParameter(queryClient, name, value);

        const finalLabel = [genericLabel, customizedLabel].filter(Boolean).join(" : ");
        if (!finalLabel) return undefined;

        return { name, label: finalLabel };
      })
      .sortBy(({ name }) => PARAMETER_ORDER[name])
      .map(({ label }) => label)
      .value();

    return [title, ...parameters, APP_NAME].filter(Boolean).join(" | ");
  };

function formatParameter(
  queryClient: QueryClient,
  name: keyof RouterI18nParameters,
  value: string,
): string | undefined {
  switch (name) {
    case "siteId": {
      const siteId = Number(value) as SiteId;
      if (!Number.isInteger(siteId) || siteId <= 0) return undefined;
      const siteData = queryClient.getQueryData(siteDetailsOptions(siteId).queryKey);

      if (!siteData || !siteData.subscriber) return undefined;

      return [siteData.subscriber.firstname, siteData.subscriber.lastname].filter(Boolean).join(" ");
    }
    case "organizationId": {
      const organizationId = Number(value);
      if (!Number.isInteger(organizationId) || organizationId <= 0) return undefined;

      const organizationData = queryClient.getQueryData(kiliOrganizationDetailsOptions(organizationId).queryKey);

      return organizationData?.name;
    }
    default: {
      // Should not interrupt in any way
      warnUnreachable(name);
      return undefined;
    }
  }
}
